import React, { useState } from "react";
import { RecoveryPhraseInput } from "./RecoveryPhraseBackup";
import { recoveryPhraseToSpendingKey } from "../../joinsplit-v2/recoveryPhrase";
import {
  hasActiveMasterRecoverySession,
  hasAnyStoredMasterRecoverySession,
} from "../../joinsplit-v2/masterRecoveryStorage";
import type {
  EIP6963ProviderDetail,
  EIP1193Provider,
} from "../hooks/useEIP6963";

interface WalletModalProps {
  providers: EIP6963ProviderDetail[];
  onSelect: (
    provider: EIP1193Provider,
    info: EIP6963ProviderDetail["info"],
    options: {
      existingWallet: boolean;
      recoveryPhrase?: string;
      legacyRecoveryPhrase?: boolean;
      linkMasterWallet?: boolean;
    },
  ) => void;
  onClose: () => void;
}

export default function WalletModal({
  providers,
  onSelect,
  onClose,
}: WalletModalProps) {
  const hasInjected = typeof (window as any).ethereum !== "undefined";
  const [restoring, setRestoring] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [error, setError] = useState("");
  const [legacyPhrase, setLegacyPhrase] = useState(false);
  const [linkExisting, setLinkExisting] = useState(false);
  const select = (
    provider: EIP1193Provider,
    info: EIP6963ProviderDetail["info"],
  ) => {
    if (restoring) {
      try {
        recoveryPhraseToSpendingKey(phrase);
      } catch {
        setError("Enter a valid 12- or 24-word StealthOS recovery phrase.");
        return;
      }
    }
    onSelect(provider, info, {
      existingWallet: false,
      ...(restoring
        ? {
            recoveryPhrase: phrase,
            ...(legacyPhrase ? { legacyRecoveryPhrase: true } : {}),
          }
        : {}),
      ...(!restoring && linkExisting ? { linkMasterWallet: true } : {}),
    });
    setPhrase("");
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(2,7,18,0.8)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--wallet-card-background, #0a1529)",
          border: "1px solid var(--wallet-border, #294063)",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: "380px",
          maxHeight: "90dvh",
          overflowY: "auto",
          margin: 16,
          boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              margin: 0,
              color: "#e8eeff",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Connect Wallet
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#b0c4de",
              fontSize: "20px",
              cursor: "pointer",
              padding: "0 4px",
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>

        <button
          type="button"
          className="wallet-secondary"
          aria-pressed={restoring}
          onClick={() => {
            setRestoring(!restoring);
            setPhrase("");
            setError("");
            setLinkExisting(false);
            setLegacyPhrase(false);
          }}
        >
          {restoring
            ? "Back to normal connection"
            : "Restore with a StealthOS recovery phrase"}
        </button>
        {!restoring &&
          !hasActiveMasterRecoverySession() &&
          hasAnyStoredMasterRecoverySession() && (
            <p className="wallet-review-context">
              A StealthOS wallet is saved on this device. Connect the same
              public wallet to unlock it. To use a different public wallet,
              restore the same StealthOS recovery phrase below, or unlock the
              original wallet first and link it.
            </p>
          )}
        {restoring && (
          <>
            <p className="wallet-review-context">
              Restore your StealthOS wallet, then connect a public wallet to use
              the app. Your private wallet is determined by this phrase.
            </p>
            <RecoveryPhraseInput
              value={phrase}
              onChange={(value) => {
                setPhrase(value);
                setError("");
              }}
              error={error}
            />
            <details style={{ marginTop: 12 }}>
              <summary>Older chain-specific wallet</summary>
              <label style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={legacyPhrase}
                  onChange={(event) => setLegacyPhrase(event.target.checked)}
                />
                This is an older chain-specific 12-word phrase.
              </label>
            </details>
          </>
        )}
        {!restoring && hasActiveMasterRecoverySession() && (
          <label
            className="wallet-review-context"
            style={{ display: "flex", gap: 8, marginTop: 16 }}
          >
            <input
              type="checkbox"
              checked={linkExisting}
              onChange={(event) => setLinkExisting(event.target.checked)}
            />
            Use my existing StealthOS wallet and recovery phrase.
          </label>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginTop: 16,
          }}
        >
          {providers.map((p) => (
            <button
              key={p.info.uuid}
              onClick={() => select(p.provider, p.info)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                width: "100%",
                padding: "12px 14px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "12px",
                color: "#e8eeff",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "background 0.15s, border-color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
              }}
            >
              <img
                src={p.info.icon}
                alt={p.info.name}
                style={{ width: 32, height: 32, borderRadius: "8px" }}
              />
              <span>{p.info.name}</span>
            </button>
          ))}

          {providers.length === 0 && hasInjected && (
            <button
              onClick={() => {
                const injected = (window as any).ethereum;
                select(injected, {
                  uuid: "injected-fallback",
                  name: "Browser Wallet",
                  icon: "",
                  rdns: "injected",
                });
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                width: "100%",
                padding: "12px 14px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "12px",
                color: "#e8eeff",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  background: "rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "16px",
                }}
              >
                &#9878;
              </div>
              <span>Browser Wallet</span>
            </button>
          )}

          {providers.length === 0 && !hasInjected && (
            <div
              style={{
                textAlign: "center",
                padding: "20px 0",
                color: "#b0c4de",
                fontSize: "13px",
              }}
            >
              No wallets detected. Install a compatible EVM wallet (MetaMask,
              Coinbase Wallet, Rabby, etc.) and refresh.
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: "16px",
            textAlign: "center",
            color: "#4a5e82",
            fontSize: "11px",
          }}
        >
          Powered by EIP-6963 multi-wallet discovery
        </div>
      </div>
    </div>
  );
}
